// PATH: src/components/layout/Footer/RealEstateFooter.jsx
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "app/providers/ThemeProvider";
import "./RealEstateFooter.css";

const LegalLinks = lazy(() => import("./RealEstateFooterLegal"));

const ROUTES = {
    home: "/",
    compra: "/compra",
    venta: "/venta",
    renta: "/renta",
    nosotros: "/nosotros",
    contacto: "/contacto",
    contactoSeccion: "/contacto/seccion",
};

function getScrollHost() {
    const container = document.querySelector(".App");
    return { container, host: container || window };
}

function getNavHeight() {
    if (typeof window === "undefined") return 76;
    const raw = window.getComputedStyle(document.documentElement).getPropertyValue("--navbar-height");
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 76;
}

function scrollToTop(behavior = "smooth") {
    const { container } = getScrollHost();
    if (container) container.scrollTo({ top: 0, behavior });
    else window.scrollTo({ top: 0, behavior });
}

function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return false;

    const navH = getNavHeight();
    const { container } = getScrollHost();

    if (container) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const top = elRect.top - containerRect.top + container.scrollTop - navH;
        container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return true;
    }

    const top = el.getBoundingClientRect().top + window.scrollY - navH;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    return true;
}

const DEFAULT_REVEAL_MAX = 520;

export default function RealEstateFooter() {
    const { darkMode } = useTheme();
    const location = useLocation();
    const rootRef = useRef(null);
    const [p, setP] = useState(0);
    const year = useMemo(() => new Date().getFullYear(), []);

    useEffect(() => {
        const { container, host } = getScrollHost();
        let raf = 0;

        const readScroll = () => (container ? container.scrollTop : window.scrollY);
        const onScroll = () => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                const y = readScroll();
                const next = Math.max(0, Math.min(1, y / DEFAULT_REVEAL_MAX));
                setP((prev) => (prev === next ? prev : next));
                if (rootRef.current) rootRef.current.style.setProperty("--re-fp", String(next));
            });
        };

        host.addEventListener("scroll", onScroll, { passive: true });
        onScroll();

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            host.removeEventListener("scroll", onScroll);
        };
    }, []);

    const isHome = location.pathname === ROUTES.home;

    const brand = useMemo(
        () => ({
            name: "GRUPO ARCA C&N",
            tagline: "Representación privada. Estándares editoriales. Cierre impecable.",
            micro: "Selección estricta • Discreción • Trazabilidad",
        }),
        []
    );

    const socials = useMemo(
        () => [
            { label: "Instagram", href: "https://www.instagram.com/", text: "ig" },
            { label: "LinkedIn", href: "https://www.linkedin.com/", text: "in" },
            { label: "Facebook", href: "https://www.facebook.com/", text: "fb" },
        ],
        []
    );

    const navLinks = useMemo(
        () => [
            { label: "Compra", to: ROUTES.compra },
            { label: "Venta", to: ROUTES.venta },
            { label: "Renta", to: ROUTES.renta },
            { label: "Nosotros", to: ROUTES.nosotros },
        ],
        []
    );

    const onHomeScroll = useCallback(
        (id) => () => {
            if (id === "top") scrollToTop("smooth");
            else scrollToId(id);
        },
        []
    );

    const contact = useMemo(
        () => ({
            city: "Ciudad de México, México",
            phone: "+52 55 1234 5678",
            phoneHref: "tel:+525512345678",
            mail: "contacto@grupoarca.com",
            mailHref: "mailto:contacto@grupoarca.com",
            hours: "Lun–Sáb · 9:00–19:00",
        }),
        []
    );

    const trust = useMemo(
        () => [
            { k: "Selección", v: "Inventario limitado" },
            { k: "Proceso", v: "Documentación clara" },
            { k: "Cierre", v: "Acompañamiento total" },
        ],
        []
    );

    const ctaLabel = useMemo(() => (p < 0.5 ? "Solicitar asesoría privada" : "Agenda una visita"), [p]);

    return (
        <footer
            ref={rootRef}
            className={`ReFooter ${darkMode ? "is-dark" : "is-light"}`}
            aria-label="Pie de página inmobiliaria"
        >
            <div className="ReFooter__topRule" aria-hidden="true" />

            <div className="ReFooter__shell">
                <div className="ReFooter__grid">
                    <section className="ReFooter__brand" aria-label="Marca">
                        <div className="ReFooter__brandRow">
                            <span className="ReFooter__mark" aria-hidden="true" />
                            <h2 className="ReFooter__brandName">{brand.name}</h2>
                        </div>

                        <p className="ReFooter__tagline">{brand.tagline}</p>

                        <div className="ReFooter__social" aria-label="Redes sociales">
                            {socials.map((s) => (
                                <a
                                    key={s.label}
                                    className="ReFooter__socialBtn"
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                >
                                    {s.text}
                                </a>
                            ))}
                        </div>

                        <div className="ReFooter__signature" aria-hidden="true">
                            <span className="ReFooter__sigLine" />
                            <span className="ReFooter__sigText">{brand.micro}</span>
                        </div>
                    </section>

                    <nav className="ReFooter__col" aria-label="Navegación">
                        <p className="ReFooter__colTitle">Navegación</p>

                        <ul className="ReFooter__list">
                            <li>
                                {isHome ? (
                                    <button type="button" className="ReFooter__link ReFooter__linkBtn" onClick={onHomeScroll("top")}>
                                        Inicio
                                    </button>
                                ) : (
                                    <RouterLink className="ReFooter__link" to={ROUTES.home}>
                                        Inicio
                                    </RouterLink>
                                )}
                            </li>

                            {navLinks.map((n) => (
                                <li key={n.to}>
                                    <RouterLink className="ReFooter__link" to={n.to}>
                                        {n.label}
                                    </RouterLink>
                                </li>
                            ))}

                            <li>
                                {isHome ? (
                                    <button type="button" className="ReFooter__link ReFooter__linkBtn" onClick={onHomeScroll("contact")}>
                                        Contacto
                                    </button>
                                ) : (
                                    <RouterLink className="ReFooter__link" to={ROUTES.contactoSeccion}>
                                        Contacto
                                    </RouterLink>
                                )}
                            </li>
                        </ul>
                    </nav>

                    <section className="ReFooter__col" aria-label="Contacto">
                        <p className="ReFooter__colTitle">Contacto</p>

                        <ul className="ReFooter__list ReFooter__list--dense">
                            <li className="ReFooter__item">
                                <span className="ReFooter__k">Ubicación</span>
                                <span className="ReFooter__v">{contact.city}</span>
                            </li>

                            <li className="ReFooter__item">
                                <span className="ReFooter__k">Teléfono</span>
                                <a className="ReFooter__link ReFooter__vLink" href={contact.phoneHref}>
                                    {contact.phone}
                                </a>
                            </li>

                            <li className="ReFooter__item">
                                <span className="ReFooter__k">Email</span>
                                <a className="ReFooter__link ReFooter__vLink" href={contact.mailHref}>
                                    {contact.mail}
                                </a>
                            </li>

                            <li className="ReFooter__item">
                                <span className="ReFooter__k">Horario</span>
                                <span className="ReFooter__v">{contact.hours}</span>
                            </li>
                        </ul>

                        <div className="ReFooter__cta">
                            <RouterLink className="ReFooter__btn ReFooter__btn--primary" to={ROUTES.contacto} aria-label={ctaLabel}>
                                {ctaLabel}
                            </RouterLink>

                            <a className="ReFooter__btn ReFooter__btn--ghost" href={contact.phoneHref} aria-label="Llamar ahora">
                                Llamar
                            </a>
                        </div>

                        <div className="ReFooter__trust" aria-label="Autoridad y garantías">
                            {trust.map((t) => (
                                <div key={t.k} className="ReFooter__trustPill">
                                    <span className="ReFooter__trustK">{t.k}</span>
                                    <span className="ReFooter__trustV">{t.v}</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="ReFooter__col" aria-label="Legal">
                        <p className="ReFooter__colTitle">Legal</p>

                        <Suspense fallback={<div className="ReFooter__legalFallback" aria-hidden="true" />}>
                            <LegalLinks />
                        </Suspense>

                        <div className="ReFooter__legalNote" aria-label="Nota legal">
                            La información es referencial y puede cambiar sin previo aviso.
                        </div>
                    </section>
                </div>
            </div>

            <div className="ReFooter__bottom">
                <div className="ReFooter__bottomInner">
                    <p className="ReFooter__copy">© {year} GRUPO ARCA C&N. Todos los derechos reservados.</p>
                    <div className="ReFooter__bottomRight">
                        <button type="button" className="ReFooter__toTop" onClick={scrollToTop} aria-label="Volver arriba">
                            Volver arriba
                        </button>
                        <span className="ReFooter__dot" aria-hidden="true" />
                        <p className="ReFooter__micro">Atención por cita • Selección limitada • Discreción total</p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
